import React, {useCallback, useState} from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    Button, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl,
    IconButton, InputLabel,
    MenuItem, Select, Switch,
    Tooltip
} from "@mui/material";

import {BRIDGE_TOKEN, CUR_CODES, EXCHANGE, HOUR_REWARD, LOCALES, PRICE_FRAME} from "../global/Consts";
import {
    DataGridPro, GridColDef, GridRenderCellParams, GridRowParams,
    GridActionsCellItem, GridRowId, DataGridProProps, GridRowModel
} from "@mui/x-data-grid-pro";
import {IAddress, IConfig} from "../global/Interface";
import DeleteIcon from '@mui/icons-material/Delete';
import AddAddress from "../elements/AddAddress";
import {useConfigContext} from "../context/Config";


export let LOCALE = 'en-us';

export default function Config() {
    const {config: config_org, updateConfig} = useConfigContext();
    const [config, setConfig] = useState<IConfig | undefined>(config_org);
    const [open, setOpen] = useState(false);

    const groupingColDef: DataGridProProps['groupingColDef'] = {
        headerName: 'Address',
        width: 450,
        cellClassName: 'Setting AddressS',
    };

    const columns: GridColDef[] = React.useMemo(
        () => [
            {
                field: 'label',
                headerName: 'Label',
                width: 200,
                cellClassName: 'Setting AddressS',
                editable: true,
                sortable: false,
            },
            {
                field: 'enabled',
                headerName: 'Enabled',
                width: 150,
                sortable: false,
                renderCell: (cell: GridRenderCellParams<IAddress>) =>
                    cell.id.toString().startsWith('auto-generated-row') ? null :
                        (<Switch checked={cell.row.enabled} onChange={toggleAdr(cell.id)}/>)
            },
            {
                headerName: 'Remove',
                field: 'actions',
                type: 'actions',
                sortable: false,
                getActions: (params: GridRowParams) =>
                    params.id.toString().startsWith('auto-generated-row') ? [] :
                        [<GridActionsCellItem
                            icon={<DeleteIcon/>}
                            label="Delete"
                            onClick={deleteAdr(params.id)}
                        />,
                        ]
            }
        ], []);

    const deleteAdr = useCallback(
        (id: GridRowId) => () => {
            setTimeout(() => {
                setConfig((prevConfig) =>
                    ({
                        ...prevConfig!,
                        addresses: prevConfig!.addresses.filter((adr) => adr.value !== id)
                    })
                );
            });
        },
        [],
    );

    const toggleAdr = useCallback(
        (id: GridRowId) => () => {
            setTimeout(() => {
                setConfig((prevConfig) =>
                    ({
                        ...prevConfig!,
                        addresses: prevConfig!.addresses.map(adr => ({
                            ...adr,
                            enabled: adr.value === id ? !adr.enabled : adr.enabled
                        })),
                    })
                );
            });
        },
        [],
    );

    function handleOpen() {
        setOpen(true);
        setConfig(config_org);
    }

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = () => {
        updateConfig({...config!, addresses: [...config!.addresses]}).then();
        handleClose();
    };

    const getTreeDataPath = (row: GridRowModel) => row.label ? [row.label, row.value] : [row.value];

    return (
        <>
            <IconButton onClick={handleOpen}>
                <Tooltip title="Change Setting">
                    <SettingsIcon
                        color={"primary"}
                        fontSize={"large"}/>
                </Tooltip>
            </IconButton>
            <Dialog
                fullWidth={true}
                maxWidth={"xl"}
                open={open}
                onClose={handleClose}
                PaperProps={{sx: {position: 'fixed', top: 50, maxHeight: '80%'}}}
            >
                <DialogTitle>Settings</DialogTitle>
                <DialogContent>
                    <DialogContent dividers>

                        <FormControl>
                            <InputLabel id="currency-label">Currency</InputLabel>
                            <Select
                                labelId="currency-label"
                                label="Currency"
                                value={config?.cur_code}
                                onChange={(item) => setConfig((c) =>
                                    ({...c!, cur_code: item.target.value}))}
                            >
                                {CUR_CODES.map((code) => (
                                    <MenuItem key={code.value}
                                              value={code.value}>{code.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        &#160;
                        <FormControl>
                            <InputLabel id="local-label">Local</InputLabel>
                            <Select
                                labelId="local-label"
                                label="Local"
                                value={config?.locale}
                                onChange={(item) => setConfig((c) =>
                                    ({...c!, locale: item.target.value}))}
                            >
                                {LOCALES.map((code) => (
                                    <MenuItem key={code.value}
                                              value={code.value}>{code.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogContent dividers>
                        <FormControl>
                            <InputLabel id="bridge_token-label">BridgeToken</InputLabel>
                            <Select
                                labelId="bridge_token-label"
                                label="BridgeToken"
                                value={config?.pfe.bridge_token}
                                onChange={(item) => setConfig((c) =>
                                    ({...c!, pfe: {...c!.pfe, bridge_token: item.target.value}}))}
                            >
                                {BRIDGE_TOKEN.map((code) => (
                                    <MenuItem key={code.value}
                                              value={code.value}>{code.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        &#160;
                        <FormControl>
                            <InputLabel id="exchange-label">Exchange</InputLabel>
                            <Select
                                labelId="exchange-label"
                                label="Exchange"
                                value={config?.pfe.exchange}
                                onChange={(item) => setConfig((c) =>
                                    ({...c!, pfe: {...c!.pfe, exchange: item.target.value}}))}
                            >
                                {EXCHANGE.map((code) => (
                                    <MenuItem key={code.value}
                                              value={code.value}>{code.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        &#160;
                        <FormControl>
                            <InputLabel id="hour_reward-label">HourReward</InputLabel>
                            <Select
                                labelId="hour_reward-label"
                                label="HourReward"
                                value={config?.pfe.hour_reward}
                                onChange={(item) => setConfig((c) =>
                                    ({...c!, pfe: {...c!.pfe, hour_reward: item.target.value as number}}))}
                            >
                                {HOUR_REWARD.map((code) => (
                                    <MenuItem key={code.value}
                                              value={code.value}>{code.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        &#160;
                        <FormControl>
                            <InputLabel id="price_frame-label">PriceFrame</InputLabel>
                            <Select
                                labelId="price_frame-label"
                                label="PriceFrame"
                                value={config?.pfe.price_frame}
                                onChange={(item) => setConfig((c) =>
                                    ({...c!, pfe: {...c!.pfe, price_frame: item.target.value}}))}
                            >
                                {PRICE_FRAME.map((code) => (
                                    <MenuItem key={code.value}
                                              value={code.value}>{code.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogContent dividers>
                        <AddAddress
                            addresses={config?.addresses ?? []}
                            onChange={adrs => setConfig(conf => ({...conf!, addresses: adrs}))}/>
                        <div className="DataGrid">
                            <DataGridPro
                                columns={columns}
                                autoHeight={true}
                                hideFooter={true}
                                getRowId={(row) => row.value}
                                rows={config?.addresses ?? []}

                                treeData={true}
                                getTreeDataPath={getTreeDataPath}
                                groupingColDef={groupingColDef}
                                defaultGroupingExpansionDepth={1}

                                isCellEditable={(params) => !params.id.toString().startsWith('auto-generated-row')}
                                onCellEditCommit={({id, value}) => setConfig((config) => {
                                    config!.addresses.find((adr) => adr.value === id)!.label = value!.toString();
                                    return config;
                                })}
                            />
                        </div>
                    </DialogContent>
                </DialogContent>

                <DialogActions>
                    <Button autoFocus onClick={handleSave}>
                        Save changes
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}